/**
 * ReauthModal — Iter-221 — Shared re-authentication framework.
 *
 * Mounted globally next to AgreementGateModal. Listens for
 * `reauth-required` window events dispatched by:
 *   • `useAutosave` when a save fails with 401 on a non-session endpoint.
 *   • Any sensitive-edit code path that wants to confirm the user's
 *     password before persisting (password change, payout details,
 *     SSN/tax info, account deletion).
 *
 * On successful re-auth, dispatches `reauth-success` so the calling
 * component / autosave hook can transparently retry the save.
 *
 * Re-auth flow:
 *   1. Receive `reauth-required` event → store reason, open modal.
 *   2. User types current password → click "Confirm".
 *   3. We POST /auth/login with the current user's email + entered
 *      password. The backend either:
 *        • returns 200 → refresh the JWT in localStorage (extends session)
 *          + dispatch `reauth-success` + close modal.
 *        • returns 401 → show inline error, keep modal open.
 *   4. Cancel → dispatch `reauth-cancelled` so the caller can revert
 *      the in-flight save / draft.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Shield, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../utils/api';

const ReauthModal = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(null);
  const [scope, setScope] = useState(null);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onRequired = (e) => {
      setReason(e.detail?.reason || null);
      setScope(e.detail?.scope || null);
      setPassword('');
      setError(null);
      setOpen(true);
    };
    window.addEventListener('reauth-required', onRequired);
    return () => window.removeEventListener('reauth-required', onRequired);
  }, []);

  const close = (success = false) => {
    setOpen(false);
    setPassword('');
    setError(null);
    if (!success && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('reauth-cancelled', { detail: { scope } }));
    }
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!user?.email || !password) {
      setError('Enter your current password to continue.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await authAPI.login({ email: user.email, password });
      const newToken = res?.data?.token;
      if (newToken) {
        localStorage.setItem('token', newToken);
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('reauth-success', { detail: { scope } }));
      }
      close(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setError('Incorrect password. Please try again.');
      } else {
        setError(err?.response?.data?.detail || 'Could not verify — please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(false); }}>
      <DialogContent className="sm:max-w-md" data-testid="reauth-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-cyan-600" />
            Confirm your password
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            {reason || "For your security, please confirm your password to continue."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="reauth-password" className="text-xs text-gray-600 uppercase tracking-wide">
              Current password
            </Label>
            <Input
              id="reauth-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              autoComplete="current-password"
              data-testid="reauth-password-input"
              disabled={submitting}
            />
          </div>
          {error && (
            <div
              className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2"
              role="alert"
              data-testid="reauth-error"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => close(false)}
              disabled={submitting}
              data-testid="reauth-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !password}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="reauth-confirm-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              Confirm
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReauthModal;
